// `/pure` deliberately: the default entry injects js.stripe.com as a module-evaluation
// side effect, and this module is imported by the [bookingType] layout provider — that
// would pull Stripe.js (and its fingerprint frame + cookies) onto every search, hotel
// details and booking page for every user. `/pure` has the identical API and loads only
// when loadStripe() is actually called.
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";

/**
 * Stripe 3DS challenge completion for the booking flow (GON-6638 PR 3).
 *
 * The book-time fraud gate verifies a new account's card server-side. When the issuer wants a
 * cardholder challenge, the booking is refused with a SetupIntent `client_secret` instead of a
 * dead end. We render the challenge here and the caller retries the booking — the retry IS the
 * completion signal: the backend re-reads the intent from Stripe (never trusting this client)
 * and lets the booking through only if Stripe says it succeeded.
 *
 * No `<Elements>` provider is needed: `handleNextAction` renders its own iframe/modal and only
 * requires the Stripe instance plus the server-issued secret. The card itself is never handled
 * here — VGS owns card collection and Stripe never sees a PAN in this flow.
 */

const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Whether this client can render a challenge at all. Sent to the backend as
 * `supports_three_ds_challenge` — declaring false makes the backend keep today's behavior
 * (cancel the intent, plain decline), so a missing publishable key degrades to the pre-3DS
 * experience instead of a challenge we could never show.
 */
export function canRenderThreeDsChallenge(): boolean {
  return Boolean(publishableKey);
}

function getStripe(): Promise<Stripe | null> {
  if (!publishableKey) {
    return Promise.resolve(null);
  }
  // Memoized so only the first challenge pays the network cost — but a FAILED load is never
  // cached. loadStripe REJECTS when js.stripe.com is blocked (ad blocker, corporate proxy);
  // stripe-js clears its own cache on error precisely so a later attempt can retry, and caching
  // the rejected promise here would defeat that for the rest of the tab.
  stripePromise =
    stripePromise ??
    loadStripe(publishableKey).catch(() => {
      stripePromise = null;
      return null;
    });
  return stripePromise;
}

export type ThreeDsChallengeOutcome = "completed" | "failed" | "unavailable";

/**
 * Run the Stripe challenge for a refused booking.
 *
 * Returns "completed" when Stripe reports the flow finished — the caller should retry the
 * booking with the SAME bookingRequestId (the refused attempt created no booking row, and
 * reusing the id preserves the double-submit guard). "completed" is deliberately NOT a claim
 * that authentication succeeded: the backend decides that from Stripe's own record on retry.
 *
 * "failed" means the user dismissed or the challenge errored; "unavailable" means Stripe.js
 * could not load (missing key, or a blocked/failed script — loadStripe REJECTS in that case,
 * which is why the load is guarded). Both leave the caller on the decline path. This never
 * throws: it runs inside the booking flow's catch block, where a throw would escape it.
 */
export async function completeThreeDsChallenge(
  clientSecret: string
): Promise<ThreeDsChallengeOutcome> {
  // Guarded even though getStripe() resolves null rather than rejecting: this runs INSIDE the
  // booking flow's catch block, so anything thrown here escapes that catch entirely and becomes
  // an unhandled rejection — freezing the booking page with its loader mounted and no error.
  let stripe: Stripe | null = null;
  try {
    stripe = await getStripe();
  } catch {
    return "unavailable";
  }
  if (!stripe) {
    return "unavailable";
  }

  try {
    const { error } = await stripe.handleNextAction({ clientSecret });
    // An error here is a dismissed or failed challenge. The SetupIntent's real state lives
    // server-side either way, so we don't inspect the returned intent — we just stop.
    return error ? "failed" : "completed";
  } catch {
    return "failed";
  }
}
